
import CreateEditorModal from "./createEditor";
import { useEffect, useMemo, useState } from "react";
import { AdService } from "@services/ads/AdService";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { Ad } from "@models/Ad.model";
import { Brand, BrandAssets, FontVariant } from "@models/brand.model";
import { brandLogoImages } from "@models/brandVariants";
import { hexToRgb } from "../../shared/utils/common";
import { appStore } from "@store/appStore";


interface AdEditorModalProps {
    adId: number
    brand: Brand
    modalOpen: boolean
    setModalOpen: (isOpen: boolean) => void
    launchId?: number
    organizationId?: number
    // Present when opened from an ad group — a fork then replaces the ad in that ad
    // group rather than only landing in the catalog.
    adGroupId?: number
}

function brandToBrandAssets(brand: Brand): BrandAssets {
    const colors = [];
    if (brand.primary_color_code) {
        colors.push({ name: "Primary", value: JSON.parse(hexToRgb(brand.primary_color_code)) });
    }
    if (brand.secondary_color_code) {
        colors.push({ name: "Secondary", value: JSON.parse(hexToRgb(brand.secondary_color_code)) });
    }
    if (brand.tertiary_color_code) {
        colors.push({ name: "Tertiary", value: JSON.parse(hexToRgb(brand.tertiary_color_code)) });
    }

    return {
        colors,
        images: brandLogoImages(brand),
        fonts: brand.fonts || [],
    };
}

export default function AdEditorModal({ adId, brand, modalOpen, setModalOpen, launchId, organizationId, adGroupId }: AdEditorModalProps) {
    const [ad, setAd] = useState<Ad | null>(null)
    const [orgBrandFonts, setOrgBrandFonts] = useState<FontVariant[]>([]);

    const brandAssets = useMemo(() => {
        const assets = brandToBrandAssets(brand);
        assets.fonts = [...assets.fonts, ...orgBrandFonts];
        return assets;
    }, [brand, orgBrandFonts]);

    async function loadAd(isCurrent: () => boolean) {
        const service = new AdService();

        if (adId > -1) {
            const responseAd = await service.getAd(adId, "children");
            // Two of these can be in flight at once (the effect re-runs on open and on close),
            // and whichever answers last used to win. That is how the tree could come back
            // showing an older shape than the one already on screen (MPN-1889).
            if (isCurrent()) setAd(responseAd);
        }
    }

    async function loadOrgBrandFonts() {
        try {
            const orgId = String(appStore.loggedInUser.organization.id);
            const orgService = new OrganizationService();
            const orgBrand = await orgService.getOrganizationBrand(orgId);
            if (orgBrand?.fonts) setOrgBrandFonts(orgBrand.fonts);
        } catch {
            // Org brand font fetch is non-critical
        }
    }

    useEffect(() => {
        let current = true
        loadAd(() => current)
        loadOrgBrandFonts()
        return () => { current = false }
    }, [adId, modalOpen])

    if (!ad) {
        return null
    } else {
        return (
            <CreateEditorModal modalOpen={modalOpen} setModalOpen={setModalOpen} ad={ad} brandAssets={brandAssets} adMode launchId={launchId} organizationId={organizationId} adGroupId={adGroupId} />
        )
    }
}
